import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  SharedDocumentPage as SharedDocumentPageComponent,
  type SharedDocumentState,
} from './SharedDocumentPage.component';
import {
  fetchSharedDocumentMeta,
  fetchSharedDocumentUrl,
  isGoneError,
  type SharedDocumentKind,
} from './shared-document-query';
import { decodeShareTokenKind } from './share-token-kind';

/** Estado do download dos bytes (independente do /meta). */
type BytesState = 'loading' | 'ready' | 'expired' | 'error';

/**
 * Container da visualização EXTERNA de currículo/laudo compartilhado (EU-594,
 * comentário 23123). Página PÚBLICA: o KeycloakGate usa check-sso e não força
 * login, então o usuário anônimo chega aqui sem sessão — meta e bytes vão por
 * `fetch` cru sem bearer (shared-document-query).
 *
 * - meta e bytes carregam em PARALELO; a página só fica `ready` com os dois.
 * - 410 em QUALQUER um → estado expirado (RN-38/40); o tipo p/ a copy vem do
 *   meta quando disponível, senão do payload do token decodificado no client
 *   (RN-37), com fallback em currículo.
 * - Bloqueios RN-33/34/35: Ctrl/Cmd+S e Ctrl/Cmd+P (keydown em CAPTURE) +
 *   `beforeprint` abrem o aviso; o CSS `@media print` do componente imprime
 *   página em branco; contextmenu é bloqueado na área do documento.
 * - Object URL do blob é revogado no cleanup.
 */
function SharedDocumentContainer({ token }: { token: string }) {
  const metaQuery = useQuery({
    queryKey: ['shared-document', token, 'meta'],
    queryFn: () => fetchSharedDocumentMeta(token),
    // 410 é terminal (expirado/inválido) — retry só em falha de API/rede.
    retry: (failureCount, error) => !isGoneError(error) && failureCount < 2,
    staleTime: Infinity,
  });

  // --- download do PDF (object URL é recurso, não cabe no react-query cache)
  const [bytesState, setBytesState] = useState<BytesState>('loading');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setBytesState('loading');
    setDocumentUrl(null);
    fetchSharedDocumentUrl(token)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setDocumentUrl(url);
        setBytesState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setBytesState(isGoneError(err) ? 'expired' : 'error');
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [token, reloadKey]);

  // --- estado combinado (RN-42 — expirado ganha de erro, que ganha de loading)
  const metaGone = isGoneError(metaQuery.error);
  const state: SharedDocumentState =
    metaGone || bytesState === 'expired'
      ? 'expired'
      : metaQuery.isError || bytesState === 'error'
        ? 'error'
        : metaQuery.isPending || bytesState === 'loading'
          ? 'loading'
          : 'ready';

  // Tipo p/ header e copy do expirado: meta → payload do token → currículo.
  const kind: SharedDocumentKind = metaQuery.data?.kind ?? decodeShareTokenKind(token) ?? 'resume';

  // --- aviso de bloqueio (RN-35)
  const [isNoticeOpen, setNoticeOpen] = useState(false);

  // RN-33/34 (EXT-004/005/008): atalhos de salvar/imprimir + beforeprint. Só
  // com documento visível — no expirado não há o que proteger (RN-38).
  useEffect(() => {
    if (state !== 'ready') return;
    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && (key === 's' || key === 'p')) {
        event.preventDefault();
        setNoticeOpen(true);
      }
    };
    const onBeforePrint = () => setNoticeOpen(true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('beforeprint', onBeforePrint);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('beforeprint', onBeforePrint);
    };
  }, [state]);

  function handleRetry() {
    if (metaQuery.isError) void metaQuery.refetch();
    if (bytesState === 'error') setReloadKey((k) => k + 1);
  }

  return (
    <SharedDocumentPageComponent
      kind={kind}
      candidateName={metaQuery.data?.candidateName ?? null}
      candidateEmail={metaQuery.data?.candidateEmail ?? null}
      state={state}
      documentUrl={documentUrl}
      onRetry={handleRetry}
      isNoticeOpen={isNoticeOpen}
      onNoticeOpenChange={setNoticeOpen}
      onBlockedAttempt={() => setNoticeOpen(true)}
      // RN-33 — render falhou (worker/offline): vira o NOSSO estado de erro
      // com retry; nunca o fallback "Abrir PDF" (viewer nativo baixa/imprime).
      onPdfLoadError={() => setBytesState('error')}
    />
  );
}

/** Rota pública `/compartilhado/$token` (filha direta da rootRoute). */
export function SharedDocumentRoutePage() {
  const { token } = useParams({ from: '/compartilhado/$token' });
  return <SharedDocumentContainer token={token} />;
}
